/**
 * MemoryDetailPage — /memories/:id
 * Demo page: /file-manager (item detail: content on the left, facts in a side card), /profile (DetailPage layout).
 * Inline edit (CanEditMemoriesArtifacts), promote to a wider scope (CanCreateMemoriesArtifacts),
 * delete (CanDeleteMemoriesArtifacts).
 */
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUpCircle, Pencil, Trash2 } from 'lucide-react';
import { Badge, Button, Callout, Card, ConfirmDialog, Skeleton, useToast } from '../../components/ui/index.js';
import { DetailPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/index.js';
import { describeQueryError } from '../../components/ui/QueryBoundary.js';
import { PAGE_HELP } from '../../help/index.js';
import {
  deleteMemoryMutation,
  getMemoryOptions,
  getMemoryQueryKey,
  listMemoriesInfiniteQueryKey,
  promoteMemoryMutation,
  updateMemoryMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { MemoryFactCard } from './memory-detail/MemoryFactCard.js';
import { PromoteMemoryModal } from './memory-detail/PromoteMemoryModal.js';
import {
  MEMORY_PROMOTION_TARGETS,
  MEMORY_SCOPE_LABEL,
  MEMORY_SCOPE_VARIANT,
  subjectLabel,
} from './memory-detail/memory-scope.js';

function hasQueryId(key: readonly unknown[], id: string): boolean {
  const head = key[0];
  return typeof head === 'object' && head !== null && '_id' in head && head._id === id;
}

const formatDate = (iso: string): string => new Date(iso).toLocaleString();

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-border-primary py-3 first:pt-0 last:border-0 last:pb-0">
    <dt className="text-sm text-muted">{label}</dt>
    <dd className="text-right text-sm text-heading">{children}</dd>
  </div>
);

/** `id` shows that memory without a route (a conversation's side panel); the route's `:id` otherwise. */
export const MemoryDetailPage: React.FC<{ id?: string }> = ({ id: idProp }) => {
  const params = useParams<{ id: string }>();
  const id = idProp ?? params.id ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const help = PAGE_HELP['memories'];

  const [editing, setEditing] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const query = useQuery(getMemoryOptions({ path: { id } }));
  const memory = query.data;

  const refreshLists = () => {
    queryClient.invalidateQueries({ queryKey: listMemoriesInfiniteQueryKey() });
    queryClient.invalidateQueries({ predicate: (q) => hasQueryId(q.queryKey, 'searchMemories') });
  };

  const updateMutation = useMutation({
    ...updateMemoryMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getMemoryQueryKey({ path: { id } }) });
      refreshLists();
      setEditing(false);
      notify('Memory updated', 'success');
    },
  });
  const promoteMutation = useMutation({
    ...promoteMemoryMutation(),
    onSuccess: (promoted) => {
      refreshLists();
      setPromoteOpen(false);
      notify('Memory promoted', 'success');
      if (promoted?.id) navigate(`/memories/${promoted.id}`);
    },
  });
  const deleteMutation = useMutation({
    ...deleteMemoryMutation(),
    onSuccess: () => {
      refreshLists();
      notify('Memory deleted', 'success');
      navigate('/memories');
    },
    onError: (error) => {
      setDeleteOpen(false);
      notify(error.message, 'danger');
    },
  });

  if (query.isPending) {
    return (
      <DetailPage title="Memory" helpContent={help} breadcrumbs={[{ label: 'Memories', to: '/memories' }]}>
        <Skeleton height={160} rounded="rounded-lg" />
      </DetailPage>
    );
  }
  if (query.isError || !memory) {
    return (
      <DetailPage title="Memory" helpContent={help} breadcrumbs={[{ label: 'Memories', to: '/memories' }]}>
        <Callout
          tone="danger"
          title={describeQueryError(query.error, 'the memory')}
          actions={
            <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
              Retry
            </Button>
          }
        />
      </DetailPage>
    );
  }

  const canPromote = MEMORY_PROMOTION_TARGETS[memory.scope].length > 0;

  return (
    <>
      <DetailPage
        title="Memory"
        helpContent={help}
        breadcrumbs={[{ label: 'Memories', to: '/memories' }]}
        badges={<Badge variant={MEMORY_SCOPE_VARIANT[memory.scope]}>{MEMORY_SCOPE_LABEL[memory.scope]}</Badge>}
        actions={
          <div className="flex items-center gap-2">
            {canPromote && (
              <Can policy="CanCreateMemoriesArtifacts">
                <Button variant="secondary" size="sm" onClick={() => setPromoteOpen(true)}>
                  <ArrowUpCircle size={14} aria-hidden="true" />
                  Promote
                </Button>
              </Can>
            )}
            {!editing && (
              <Can policy="CanEditMemoriesArtifacts">
                <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                  <Pencil size={14} aria-hidden="true" />
                  Edit
                </Button>
              </Can>
            )}
            <Can policy="CanDeleteMemoriesArtifacts">
              <Button variant="danger" size="sm" onClick={() => setDeleteOpen(true)}>
                <Trash2 size={14} aria-hidden="true" />
                Delete
              </Button>
            </Can>
          </div>
        }
        alert={
          memory.deletedAt ? (
            <Callout tone="warning" title={`This memory was deleted on ${formatDate(memory.deletedAt)}.`} />
          ) : undefined
        }
        rail={
          <Card title="Details">
            <dl>
              <Fact label="Scope">{MEMORY_SCOPE_LABEL[memory.scope]}</Fact>
              <Fact label="About">{subjectLabel(memory.subjectType)}</Fact>
              <Fact label="Created">{formatDate(memory.createdAt)}</Fact>
              {memory.sourceConversationId && (
                <Fact label="Source">
                  <Link
                    to={`/conversations/${memory.sourceConversationId}`}
                    className="text-accent-text hover:underline"
                  >
                    Open the conversation
                  </Link>
                </Fact>
              )}
              {memory.promotedFrom && (
                <Fact label="Promoted from">
                  <Link to={`/memories/${memory.promotedFrom}`} className="text-accent-text hover:underline">
                    Open the original memory
                  </Link>
                </Fact>
              )}
            </dl>
          </Card>
        }
      >
        <MemoryFactCard
          key={memory.factText}
          factText={memory.factText}
          editing={editing}
          onCancel={() => setEditing(false)}
          onSave={(factText) => updateMutation.mutateAsync({ path: { id }, body: { factText } })}
        />
      </DetailPage>

      <PromoteMemoryModal
        open={promoteOpen}
        currentScope={memory.scope}
        onClose={() => setPromoteOpen(false)}
        onPromote={(targetScope, newSubjectId) =>
          promoteMutation.mutateAsync({ path: { id }, body: { targetScope, newSubjectId } })
        }
      />
      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => deleteMutation.mutate({ path: { id } })}
        title="Delete this memory?"
        message="Agents stop using this fact. This cannot be undone."
        confirmLabel="Delete memory"
        loading={deleteMutation.isPending}
      />
    </>
  );
};
